import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'
import { useHistory } from 'react-router-dom'

import NumberPadV2 from './NumberPadV2'
import ChangeCalculatorField from './ChangeCalculatorField'
import useRemoteOrderTotal from '../hooks/useRemoteOrderTotal'
import Button from '../components/Button'
import SegmentedControl from './SegmentedControl'

import styles from './ChangeCalculatorForm.module.scss'
import VNConcessionsNavBar from './VNConcessionsNavBar'
import { VNORDERS_FEATURE_ENABLED } from '../VNOrders/Enums'
import useCashRounding from '../hooks/useCashRounding'
import { Info } from '@material-ui/icons'

const ChangeCalculatorForm = ({ onComplete, onBack, total, paymentId, tenderAmountInCents, order, className, ...props }) => {
  const classNames = cn(styles.changeCalculatorForm, className)
  const [input, setInput] = useState('')
  const [change, setChange] = useState(0)
  const [submitted, setSubmitted] = useState(false)
  const [loadRemoteOrderTotal, { data: remoteOrderTotal, loading }] = useRemoteOrderTotal()
  const totalInCents = remoteOrderTotal?.totalAmountInCents ?? total

  const isLastPayment = useMemo(() => {
    // If the total matches the remaining amount, this is the last payment
    return totalInCents === (order?.balanceDueInCents || 0)
  }, [totalInCents, order])

  const { cashRoundingInCents, cashRounded } = useCashRounding({ totalInCents })
  const roundedTotalInCents = totalInCents + cashRoundingInCents
  const noPaymentRequired = roundedTotalInCents === 0

  useEffect(() => {
    if (!paymentId) {
      loadRemoteOrderTotal()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onTapNumberPad = (value) => {
    const newNumber = value === 'backspace' ? input.slice(0, -1) : input + value
    const change = parseInt(newNumber) - roundedTotalInCents
    const newChange = change > 0 ? change : 0

    setInput(newNumber)
    setChange(newChange)
  }

  const onTapShortcut = (value, isExact = false) => {
    if (isExact) {
      const amount = value
      setInput(`${amount}`)
      setChange(0)
      return
    }

    const newNumber = input === '' ? value : parseInt(input) + value
    const change = parseInt(newNumber) - roundedTotalInCents
    const newChange = change > 0 ? change : 0

    setInput(`${newNumber}`)
    setChange(newChange)
  }

  const clearInput = () => {
    setChange(0)
    setInput('')
  }

  const totalLabel = loading ? '-' : `$${(parseInt(roundedTotalInCents) / 100).toFixed(2)}`
  const inputLabel = `$${(parseInt(input) / 100 || 0).toFixed(2)}`
  let changeLabel = '--'
  let changeLabelState = 'default'

  if (change > 0) {
    changeLabel = `$${(parseInt(change) / 100 || 0).toFixed(2)}`
    changeLabelState = 'prominent'
  }

  if (parseInt(input) === roundedTotalInCents) {
    changeLabel = '$0.00'
    changeLabelState = 'prominent'
  }

  useEffect(() => {
    if (submitted) return
    if (noPaymentRequired && !loading) {
      setSubmitted(true)
      onComplete(null, noPaymentRequired, cashRoundingInCents, isLastPayment)
    }
  }, [noPaymentRequired, loading, submitted, onComplete, cashRoundingInCents, isLastPayment])

  const [selectedAmountIndex, setSelectedAmountIndex] = useState(undefined)
  const isDoneButtonDisabled = (parseInt(input) || 0) < roundedTotalInCents || submitted
  const clientWidth = document?.body?.clientWidth

  const suggestedShortcutAmounts = ['$1', '$5', '$20', 'Exact']
  const suggestedShortcutAmountInCents = [100, 500, 2000, true]
  const history = useHistory()

  // for back button in cash modal, redircts directly to menu
  const onBackClick = () => {
    history.goBack()
  }

  const selectShortcutAmount = (index) => {
    let isExact = false
    let amount = suggestedShortcutAmountInCents[index]

    if (typeof amount !== 'number') {
      isExact = true
      amount = roundedTotalInCents
    }

    setSelectedAmountIndex(index)
    onTapShortcut(amount, isExact)
  }

  const handleDoneClick = () => {
    setSubmitted(true)

    if (VNORDERS_FEATURE_ENABLED) {
      onComplete(parseInt(input), noPaymentRequired, cashRoundingInCents, isLastPayment)
      return
    }

    onComplete(null, noPaymentRequired, cashRoundingInCents, isLastPayment)
  }

  return (
    <>
      {clientWidth >= 500 ? (
        <div {...props} className={classNames}>
          <VNConcessionsNavBar onClick={onBackClick} showIcon={true} />

          <div className={styles.totalLabelWrapper}>
            <div className={styles.totalLabel}>{totalLabel}</div>
            {cashRounded && (
              <div className={styles.cashRoundedLabelContainer}>
                <Info className={styles.cashRoundedLabelIcon} />
                <div>Amount has been rounded to the nearest nickel.</div>
              </div>
            )}
          </div>

          <div className={styles.changeCalculatorContainer}>
            <div className={styles.row}>
              <div className={styles.col1}>
                <div className={styles.numberPadContainer}>
                  <div className={styles.shortcutsContainer}>
                    <SegmentedControl
                      items={suggestedShortcutAmounts}
                      onChange={selectShortcutAmount}
                      selectedIndex={selectedAmountIndex}
                      className={styles.cashSuggestions}
                      color="primary"
                    />
                  </div>
                  <div className={styles.numberPad}>
                    <NumberPadV2 onTapButton={onTapNumberPad} isCashTender={true} />
                  </div>
                  <div className={styles.doneBttnContainer}>
                    <button onClick={handleDoneClick} disabled={isDoneButtonDisabled} className={styles.doneButton}>
                      Done
                    </button>
                  </div>
                </div>
              </div>

              <div className={styles.col2}>
                <div className={styles.sideBar}>
                  <ChangeCalculatorField
                    className={styles.field}
                    label="amount paid"
                    text={inputLabel}
                    state="active"
                    canClear={input !== ''}
                    onClear={clearInput}
                  />
                  <ChangeCalculatorField
                    className={styles.field}
                    label="change due"
                    text={changeLabel}
                    state={changeLabelState}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <VNConcessionsNavBar textDisplay={totalLabel} onClick={onBack} showIcon={true} />
          <div className={styles.changeCalculatorBody}>
            {cashRounded && (
              <div className={styles.cashRoundedLabelContainer}>
                <div>Amount has been rounded to the nearest nickel.</div>
              </div>
            )}
            <div className={styles.changeCalculatorTransaction}>
              <div className={styles.changeCalculatorTransactionItem}>
                <span className={styles.label}>Amount Paid:</span>
                <span className={styles.amount}>{inputLabel}</span>
              </div>
              <div className={styles.changeCalculatorTransactionItem}>
                <span className={styles.label}>Change Due:</span>
                <span className={styles.amount}>{changeLabel}</span>
              </div>
            </div>
            <div className={styles.changeCalculatorShortcuts}>
              <SegmentedControl
                items={suggestedShortcutAmounts}
                onChange={selectShortcutAmount}
                selectedIndex={selectedAmountIndex}
                className={styles.cashSuggestions}
              />
            </div>
            <div className={styles.numberPadContainer}>
              <div className={styles.numberPad}>
                <NumberPadV2 onTapButton={onTapNumberPad} isCashTender={true} />
              </div>
            </div>
          </div>
          <div className={styles.changeCalculatorFooter}>
            <Button useMui disabled={isDoneButtonDisabled} onClick={handleDoneClick} className={styles.completeButton}>
              Done
            </Button>
          </div>
        </>
      )}
    </>
  )
}

ChangeCalculatorForm.defaultProps = {
  onComplete: () => {},
  total: 0,
}

ChangeCalculatorForm.propTypes = {
  onComplete: PropTypes.func,
  total: PropTypes.number,
  order: PropTypes.object,
}

export default ChangeCalculatorForm
